import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';

type LanguageCode = string;
export default function TissTable({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    const initialColumns = data.data?.columns || [];
    const initialList = data.data?.list || [];
    const hasContent = initialColumns.length > 0 || initialList.length > 0;

    const [isEditing, setIsEditing] = React.useState(!hasContent);
    const [columns, setColumns] = React.useState<any[]>(initialColumns.length > 0 ? initialColumns : [
        { key: 'col1', label: t('Column 1') },
        { key: 'col2', label: t('Column 2') }
    ]);
    const [rows, setRows] = React.useState<any[]>(initialList.length > 0 ? initialList : [
        { col1: '', col2: '' },
        { col1: '', col2: '' }
    ]);
    const [saving, setSaving] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [editingCell, setEditingCell] = React.useState<{ row: number; col: string } | null>(null);
    const [editingHeader, setEditingHeader] = React.useState<string | null>(null);
    const [editingHeaderValue, setEditingHeaderValue] = React.useState<string>('');

    // Track changes
    React.useEffect(() => {
        const dataEqual = JSON.stringify({ columns, rows }) === JSON.stringify({ columns: initialColumns, rows: initialList });
        setHasChanges(!dataEqual);
    }, [columns, rows, initialColumns, initialList]);

    // Initialize with default table if empty
    React.useEffect(() => {
        if (!hasContent && isEditing && columns.length === 0) {
            setColumns([
                { key: 'col1', label: t('Column 1') },
                { key: 'col2', label: t('Column 2') }
            ]);
            setRows([
                { col1: '', col2: '' },
                { col1: '', col2: '' }
            ]);
        }
    }, [hasContent, isEditing]);

    const addColumn = () => {
        const newKey = `col${columns.length + 1}`;
        const newColumn = { key: newKey, label: `${t('Column')} ${columns.length + 1}` };
        setColumns([...columns, newColumn]);
        setRows(rows.map(row => ({ ...row, [newKey]: '' })));
    };

    const addRow = () => {
        const newRow = columns.reduce((acc, col) => ({ ...acc, [col.key]: '' }), {});
        setRows([...rows, newRow]);
    };

    const deleteColumn = async (columnKey: string) => {
        if (columns.length <= 1) {
            Swal.fire({
                title: t('Error'),
                text: t('Table must have at least one column'),
                icon: 'warning',
            });
            return;
        }

        const result = await Swal.fire({
            title: t('Delete column?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            setColumns(columns.filter(col => col.key !== columnKey));
            setRows(rows.map(row => {
                const newRow = { ...row };
                delete newRow[columnKey];
                return newRow;
            }));
        }
    };

    const deleteRow = async (rowIndex: number) => {
        if (rows.length <= 1) {
            Swal.fire({
                title: t('Error'),
                text: t('Table must have at least one row'),
                icon: 'warning',
            });
            return;
        }

        const result = await Swal.fire({
            title: t('Delete row?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            setRows(rows.filter((_, i) => i !== rowIndex));
        }
    };

    const updateCell = (rowIndex: number, columnKey: string, value: string) => {
        const updatedRows = rows.map((row, idx) =>
            idx === rowIndex ? { ...row, [columnKey]: value } : row
        );
        setRows(updatedRows);
    };

    const updateHeader = (columnKey: string, newLabel: string) => {
        if (!newLabel.trim()) {
            Swal.fire({
                title: t('Error'),
                text: t('Column name cannot be empty'),
                icon: 'warning',
            });
            return;
        }
        setColumns(columns.map(col => 
            col.key === columnKey ? { ...col, label: newLabel.trim() } : col
        ));
        setEditingHeader(null);
        setEditingHeaderValue('');
    };

    const save = async () => {
        if (!data?._id) return;

        setSaving(true);
        try {
            await axios.put(
                `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
                { list: rows, columns },
                {
                    headers: {
                        Authorization: `Bearer ${cookie['token']}`,
                    },
                }
            );

            Swal.fire({
                title: t('Saved successfully'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            setIsEditing(false);
            setHasChanges(false);
            if (reload) reload();
        } catch (error: any) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: t('Error saving'),
                icon: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            Swal.fire({
                title: t('Discard changes?'),
                text: t('You have unsaved changes. Are you sure you want to cancel?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('Yes, discard'),
                cancelButtonText: t('No, keep editing'),
            }).then((result) => {
                if (result.isConfirmed) {
                    setColumns(initialColumns.length > 0 ? initialColumns : [
                        { key: 'col1', label: t('Column 1') },
                        { key: 'col2', label: t('Column 2') }
                    ]);
                    setRows(initialList.length > 0 ? initialList : [
                        { col1: '', col2: '' },
                        { col1: '', col2: '' }
                    ]);
                    setIsEditing(false);
                    setHasChanges(false);
                }
            });
        } else {
            setIsEditing(false);
        }
    };

    // Empty state
    if (!hasContent && !isEditing) {
        return (
            <div
                className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
                dir="rtl"
            >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                    {t('Click to add table')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                    {t('Create a data table')}
                </span>
            </div>
        );
    }

    // Preview Mode
    if (!isEditing) {
        return (
            <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
                <div className="p-0.5 relative">
                    {rows.length > 0 && columns.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-gray-100">
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700 bg-gray-50"
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                                            {columns.map((col) => (
                                                <td
                                                    key={col.key}
                                                    className="border border-gray-300 px-4 py-3 text-right text-gray-700"
                                                >
                                                    {row[col.key] || '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                <p className="text-sm">{t('No table data')}</p>
                            </div>
                        </div>
                    )}

                    {/* Edit Button - Appears on hover */}
                    {rows.length > 0 && columns.length > 0 && (
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('Edit Table')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
            <div className="p-0.5">
                <div className="space-y-4">
                    {/* Quick Actions */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={addColumn}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('Add Column')}
                        </button>
                        <button
                            onClick={addRow}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('Add Row')}
                        </button>
                    </div>

                    {/* Table Editor */}
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="w-full border-collapse bg-white">
                            <thead>
                                <tr className="bg-gray-50 border-b-2 border-gray-300">
                                    {columns.map((col) => (
                                        <th
                                            key={col.key}
                                            className="border border-gray-200 px-3 py-3 text-right min-w-[150px] relative group/header"
                                        >
                                            {editingHeader === col.key ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={editingHeaderValue}
                                                        onChange={(value) => setEditingHeaderValue(value || '')}
                                                        onPressEnter={() => updateHeader(col.key, editingHeaderValue)}
                                                        className="flex-1"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: '1px solid #3b82f6',
                                                            borderRadius: '6px',
                                                            textAlign: 'right',
                                                        }}
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => updateHeader(col.key, editingHeaderValue)}
                                                        className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingHeader(null);
                                                            setEditingHeaderValue('');
                                                        }}
                                                        className="p-1.5 bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between">
                                                    <span
                                                        className="flex-1 cursor-pointer hover:text-blue-600 transition-colors font-semibold text-gray-700"
                                                        onClick={() => {
                                                            setEditingHeader(col.key);
                                                            setEditingHeaderValue(col.label);
                                                        }}
                                                    >
                                                        {col.label}
                                                    </span>
                                                    {columns.length > 1 && (
                                                        <button
                                                            onClick={() => deleteColumn(col.key)}
                                                            className="opacity-0 group-hover/header:opacity-100 p-1 hover:bg-red-100 text-red-600 rounded transition-all"
                                                            title={t('Delete column')}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th className="border border-gray-200 px-3 py-3 bg-gray-50 w-16"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rowIndex) => (
                                    <tr key={rowIndex} className="border-b border-gray-200 hover:bg-gray-50 transition-colors group/row">
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className="border border-gray-200 px-3 py-2"
                                            >
                                                {editingCell?.row === rowIndex && editingCell?.col === col.key ? (
                                                    <Input
                                                        value={row[col.key] || ''}
                                                        onChange={(value) => updateCell(rowIndex, col.key, value || '')}
                                                        onBlur={() => setEditingCell(null)}
                                                        onPressEnter={() => setEditingCell(null)}
                                                        className="w-full"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: '1px solid #3b82f6',
                                                            borderRadius: '6px',
                                                            textAlign: 'right',
                                                        }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <div
                                                        className="px-3 py-2 min-h-[36px] cursor-pointer hover:bg-blue-50 rounded transition-colors text-gray-700"
                                                        onClick={() => setEditingCell({ row: rowIndex, col: col.key })}
                                                    >
                                                        {row[col.key] || <span className="text-gray-400 italic">{t('Click to edit')}</span>}
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                        <td className="border border-gray-200 px-2 py-2 bg-gray-50">
                                            {rows.length > 1 && (
                                                <button
                                                    onClick={() => deleteRow(rowIndex)}
                                                    className="opacity-0 group-hover/row:opacity-100 p-1.5 hover:bg-red-100 text-red-600 rounded transition-all"
                                                    title={t('Delete row')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 justify-end items-center">
                    {hasChanges && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                            {t('Unsaved changes')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !hasChanges}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>{t('Saving...')}</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{t('Save')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
